import React, { useState } from 'react';
import { RotateCcw, AlertCircle, CheckCircle, Hash } from 'lucide-react';

const RefundModal = ({ onClose, onSubmit, currency }) => {
  const [orderId, setOrderId] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!orderId?.trim()) { setError('Please enter an order ID'); return; }
    if (!amount || parseFloat(amount) <= 0) { setError('Please enter a valid refund amount'); return; }
    if (!reason?.trim()) { setError('Please provide a reason for the refund'); return; }

    setSubmitting(true);
    try {
      await onSubmit?.({ orderId, amount: parseFloat(amount), reason });
      setSuccess(true);
      setTimeout(() => { setSuccess(false); onClose?.(); }, 2500);
    } catch (err) {
      setError(err?.message || 'Failed to process refund');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#0d1f3c] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl">
        {/* Header */}
        <div className="flex items-center gap-3 p-5 border-b border-white/10">
          <div className="w-10 h-10 rounded-xl bg-yellow-500/20 flex items-center justify-center">
            <RotateCcw className="w-5 h-5 text-yellow-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold">Initiate Refund</h3>
            <p className="text-white/40 text-xs">Admin action — creates ledger adjustment</p>
          </div>
        </div>

        <div className="p-5">
          {success ? (
            <div className="flex flex-col items-center py-8 gap-3">
              <CheckCircle className="w-12 h-12 text-emerald-400" />
              <p className="text-white font-semibold">Refund Processed</p>
              <p className="text-white/40 text-sm text-center">Ledger entries created for customer credit and platform debit</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 bg-rose-500/20 border border-rose-500/30 rounded-xl p-3">
                  <AlertCircle className="w-4 h-4 text-rose-400 flex-shrink-0" />
                  <p className="text-rose-300 text-sm">{error}</p>
                </div>
              )}

              <div>
                <label className="text-white/60 text-xs font-medium uppercase tracking-wide mb-2 block">Order / Reference ID</label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <input
                    type="text"
                    value={orderId}
                    onChange={e => setOrderId(e?.target?.value)}
                    placeholder="Order UUID or reference"
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white text-sm focus:outline-none focus:border-[#ffd700]/50 placeholder-white/20"
                  />
                </div>
              </div>

              <div>
                <label className="text-white/60 text-xs font-medium uppercase tracking-wide mb-2 block">Refund Amount ({currency || 'KES'})</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">{currency || 'KES'}</span>
                  <input
                    type="number"
                    value={amount}
                    onChange={e => setAmount(e?.target?.value)}
                    placeholder="0.00"
                    min="1"
                    step="0.01"
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-14 pr-4 py-3 text-white text-sm focus:outline-none focus:border-[#ffd700]/50 placeholder-white/20"
                  />
                </div>
              </div>

              <div>
                <label className="text-white/60 text-xs font-medium uppercase tracking-wide mb-2 block">Reason</label>
                <textarea
                  value={reason}
                  onChange={e => setReason(e?.target?.value)}
                  placeholder="Reason for refund..."
                  rows={3}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#ffd700]/50 placeholder-white/20 resize-none"
                />
              </div>

              {/* Ledger flow preview */}
              <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                <p className="text-white/40 text-xs mb-2 font-medium">Ledger entries that will be created:</p>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-400" />
                    <span className="text-white/50 text-xs">Customer wallet → credit (refund)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-rose-400" />
                    <span className="text-white/50 text-xs">Platform wallet → debit (refund)</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
                >
                  {submitting ? (
                    <div className="w-4 h-4 border-2 border-[#0a1628]/30 border-t-[#0a1628] rounded-full animate-spin" />
                  ) : (
                    <RotateCcw className="w-4 h-4" />
                  )}
                  Process Refund
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default RefundModal;
